import * as Sentry from "@sentry/react";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { motion } from "motion/react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { confirmOrderFn } from "@/api/vendure/order/order.query.options";
import {
  PENDING_PAYMENT_KEY,
  type PendingPayment,
} from "@/api/vendure/order/order.types";
import { Button } from "@/components/ui/button";
import { CompactLoader } from "@/components/ui/loader";
import { useActivateVenue } from "@/hooks/useActivateVenue";
import { noindexHead } from "@/lib/seo";
import {
  cartCommentAtom,
  clearCartAtom,
  lastOrderCodeAtom,
  lastOrderSlugAtom,
  orderAtom,
} from "@/stores/cart.store";
import { deliveryInstructionsAtom } from "@/stores/delivery.store";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 60;

export const Route = createFileRoute("/business/$slug_/order-pending")({
  ssr: false,
  head: noindexHead,
  component: Sentry.withProfiler(OrderPendingPage, {
    name: "OrderPendingPage",
  }),
});

type PageStatus = "polling" | "failed" | "timeout";

function OrderPendingPage() {
  useActivateVenue();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams({ strict: false }) as { slug: string };
  const setOrder = useSetAtom(orderAtom);
  const setLastOrderCode = useSetAtom(lastOrderCodeAtom);
  const setLastOrderSlug = useSetAtom(lastOrderSlugAtom);
  const clearCart = useSetAtom(clearCartAtom);
  const setCartComment = useSetAtom(cartCommentAtom);
  const setDeliveryInstructions = useSetAtom(deliveryInstructionsAtom);
  const [status, setStatus] = useState<PageStatus>("polling");

  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_PAYMENT_KEY);
    if (!raw) {
      navigate({
        to: "/business/$slug/checkout",
        params: { slug },
        replace: true,
      });
      return;
    }

    const { sessionId, channelToken } = JSON.parse(raw) as PendingPayment;

    let attempts = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await confirmOrderFn(channelToken, sessionId);
        const payStatus = data.payment?.status;

        if (payStatus === "succeeded") {
          sessionStorage.removeItem(PENDING_PAYMENT_KEY);
          setOrder(data.order);
          setLastOrderCode(data.order.code);
          setLastOrderSlug(slug);
          clearCart();
          setCartComment("");
          setDeliveryInstructions("");
          navigate({
            to: "/business/$slug/order-confirm",
            params: { slug },
            search: { code: data.order.code },
            replace: true,
          });
          return;
        }

        if (payStatus === "failed" || payStatus === "requires_action") {
          sessionStorage.removeItem(PENDING_PAYMENT_KEY);
          setStatus("failed");
          return;
        }
      } catch {
        // network error — keep trying
      }

      attempts++;
      if (attempts >= POLL_MAX_ATTEMPTS) {
        sessionStorage.removeItem(PENDING_PAYMENT_KEY);
        setStatus("timeout");
        return;
      }

      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const views: Record<PageStatus, ReactNode> = {
    polling: (
      <div className="flex flex-col items-center gap-4 text-center">
        <CompactLoader />
        <p className="text-base font-semibold text-foreground">
          {t("business.orderPending.title")}
        </p>
        <p className="text-sm text-muted-foreground max-w-xs">
          {t("business.orderPending.description")}
        </p>
      </div>
    ),
    timeout: (
      <div className="flex flex-col items-center gap-5 text-center">
        <div className="space-y-2">
          <p className="text-base font-semibold text-foreground">
            {t("business.orderPending.timeout")}
          </p>
          <p className="text-sm text-muted-foreground max-w-xs">
            {t("business.orderPending.timeoutDescription")}
          </p>
        </div>
        <Button
          className="px-8"
          onClick={() =>
            navigate({ to: "/business/$slug", params: { slug }, replace: true })
          }
        >
          {t("business.orderPending.backToMenu")}
        </Button>
      </div>
    ),
    failed: (
      <div className="flex flex-col items-center gap-5 text-center">
        <div className="space-y-2">
          <p className="text-base font-semibold text-foreground">
            {t("business.orderPending.failed")}
          </p>
          <p className="text-sm text-muted-foreground max-w-xs">
            {t("business.orderPending.failedDescription")}
          </p>
        </div>
        <Button
          className="px-8"
          onClick={() =>
            navigate({
              to: "/business/$slug/checkout",
              params: { slug },
              replace: true,
            })
          }
        >
          {t("business.orderPending.tryAgain")}
        </Button>
      </div>
    ),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="min-h-screen bg-background flex flex-col items-center justify-center px-6"
    >
      {views[status]}
    </motion.div>
  );
}
